import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import api from "../api/client";
import { fmtDate } from "../lib/format";
import { Link } from "react-router-dom";
import {
  Search, FileText, Image as ImageIcon, Download, Trash2, Filter, Loader2,
  Archive, User, FileSignature,
} from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const FORMAT_BYTES = (n) => {
  if (!n) return "—";
  if (n > 1024 * 1024) return (n / 1024 / 1024).toFixed(1) + " Mo";
  if (n > 1024) return Math.round(n / 1024) + " Ko";
  return n + " o";
};

const CATEGORY_COLORS = {
  cni_recto: "bg-[#FFF4E5] text-[#7A5A1A]",
  cni_verso: "bg-[#FFF4E5] text-[#7A5A1A]",
  contrat_pdf: "bg-[#E8EBFA] text-[#1E3A8A]",
  recu_pdf: "bg-[#E5F0E8] text-[#2D5A3C]",
  rappel_pdf: "bg-[#FBEAEA] text-[#7A2D2D]",
  simulation_pdf: "bg-[#FCE7F3] text-[#9D174D]",
  facture: "bg-[#F1F3FB] text-[#0F1A3D]",
  general: "bg-[#F1F3FB] text-[#4B5775]",
  autre: "bg-[#F1F3FB] text-[#4B5775]",
};

export default function Documents() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ category: "all", owner_type: "all", auto_only: "all", q: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.category !== "all") params.category = filters.category;
      if (filters.owner_type !== "all") params.owner_type = filters.owner_type;
      if (filters.auto_only === "yes") params.auto_only = true;
      else if (filters.auto_only === "no") params.auto_only = false;
      if (filters.q) params.q = filters.q;
      const { data } = await api.get("/documents/library", { params });
      setItems(data || []);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/documents/categories");
      setCategories(data);
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(), 250);
    return () => clearTimeout(t);
  }, [filters]); // eslint-disable-line

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: d.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_name || "document";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur de téléchargement");
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Supprimer "${d.label}" ?`)) return;
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Document supprimé");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  // Counters par catégorie
  const counts = items.reduce((acc, d) => { acc[d.category] = (acc[d.category] || 0) + 1; return acc; }, {});

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-[1500px] mx-auto">
        <PageHeader
          kicker="Archives"
          title="Bibliothèque documentaire"
          subtitle="Tous vos fichiers centralisés : CNI clients, contrats, reçus, simulations… Auto-archivés à chaque génération."
        />

        {/* Filtres */}
        <div className="tpco-card p-4 mb-5 grid grid-cols-1 lg:grid-cols-5 gap-3" data-testid="docs-filters">
          <div className="lg:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3B5]" />
            <input
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Rechercher (nom, label)…"
              className="w-full h-10 pl-9 pr-3 text-sm rounded-md border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A]"
              data-testid="docs-search"
            />
          </div>
          <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
            <SelectTrigger className="h-10" data-testid="docs-filter-category"><Filter className="w-3.5 h-3.5 mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes catégories</SelectItem>
              {categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.owner_type} onValueChange={(v) => setFilters({ ...filters, owner_type: v })}>
            <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Clients & contrats</SelectItem>
              <SelectItem value="client">Clients uniquement</SelectItem>
              <SelectItem value="contrat">Contrats uniquement</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.auto_only} onValueChange={(v) => setFilters({ ...filters, auto_only: v })}>
            <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les fichiers</SelectItem>
              <SelectItem value="yes">Auto-archivés</SelectItem>
              <SelectItem value="no">Uploadés manuellement</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Quick category counters */}
        {!loading && Object.keys(counts).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(counts).map(([k, n]) => (
              <button
                key={k}
                onClick={() => setFilters({ ...filters, category: filters.category === k ? "all" : k })}
                className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded ${CATEGORY_COLORS[k] || "bg-[#F1F3FB] text-[#4B5775]"} ${filters.category === k ? "ring-2 ring-[#1E3A8A]" : ""}`}
              >
                {categories.find((c) => c.key === k)?.label || k} · {n}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-10 text-[#4B5775]"><Loader2 className="w-5 h-5 inline-block animate-spin mr-2" /> Chargement…</div>
        ) : items.length === 0 ? (
          <div className="tpco-card p-12 text-center">
            <Archive className="w-12 h-12 text-[#1E3A8A] mx-auto mb-3 opacity-50" />
            <div className="font-display text-lg font-bold text-[#0F1A3D]">Aucun document</div>
            <div className="text-sm text-[#4B5775]">Les fichiers uploadés et PDFs générés apparaîtront ici.</div>
          </div>
        ) : (
          <div className="tpco-card overflow-hidden" data-testid="docs-list">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC]">
                <tr className="text-[10px] uppercase tracking-wider text-[#4B5775] font-bold">
                  <th className="text-left px-5 py-3">Document</th>
                  <th className="text-left px-3 py-3">Catégorie</th>
                  <th className="text-left px-3 py-3">Rattaché à</th>
                  <th className="text-left px-3 py-3">Date</th>
                  <th className="text-left px-3 py-3">Taille</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F3FB]">
                {items.map((d) => {
                  const isImg = d.content_type?.startsWith("image/");
                  return (
                    <tr key={d.id} className="hover:bg-[#F8FAFC]" data-testid={`doc-${d.id}`}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          {isImg ? <ImageIcon className="w-5 h-5 text-[#1E3A8A] shrink-0" /> : <FileText className="w-5 h-5 text-[#E91E63] shrink-0" />}
                          <div className="min-w-0">
                            <div className="font-bold text-[#0F1A3D] truncate">{d.label}</div>
                            <div className="text-[11px] text-[#4B5775] truncate">{d.original_name}</div>
                          </div>
                          {d.auto_archived && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#FCE7F3] text-[#9D174D] shrink-0">Auto</span>}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${CATEGORY_COLORS[d.category] || "bg-[#F1F3FB] text-[#4B5775]"}`}>
                          {d.category_label || d.category}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs">
                        <Link
                          to={d.owner_type === "client" ? `/clients/${d.owner_id}` : `/contrats/${d.owner_id}`}
                          className="text-[#1E3A8A] hover:underline inline-flex items-center gap-1"
                        >
                          {d.owner_type === "client" ? <User className="w-3 h-3" /> : <FileSignature className="w-3 h-3" />}
                          {d.owner_name}
                        </Link>
                      </td>
                      <td className="px-3 py-3 text-xs text-[#4B5775] whitespace-nowrap">{fmtDate(d.created_at)}</td>
                      <td className="px-3 py-3 text-xs text-[#4B5775] font-mono">{FORMAT_BYTES(d.size)}</td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        <button onClick={() => download(d)} className="w-8 h-8 rounded-md border border-[#E2E5F1] hover:border-[#1E3A8A] hover:text-[#1E3A8A] text-[#4B5775] inline-flex items-center justify-center mr-1" title="Télécharger" data-testid={`doc-dl-${d.id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => remove(d)} className="w-8 h-8 rounded-md border border-[#E2E5F1] hover:border-[#C83B3B] hover:text-[#C83B3B] text-[#4B5775] inline-flex items-center justify-center" title="Supprimer" data-testid={`doc-del-${d.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
